import React, { type ReactNode } from "react";
import { Tab } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { TextColumn, type TextColumnProps } from "ui/text";
import { ProgressBar } from "./progress-bar";

export type ProgressTabProps = {
  /**
   * The color theme of the tab content.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * Determines if the tab is active. Active tabs have a progress bar.
   */
  isActive?: boolean;

  /**
   * Unique identifier for the tab.
   */
  id: string;

  /**
   * The title of the tab.
   */
  title: TextColumnProps["title"];

  /**
   * The HTML element to render the title as.
   *
   * @default "h3" is inherited by `TextColumn`
   */
  titleAs?: TextColumnProps["titleAs"];

  /**
   * The description of the tab.
   */
  description?: TextColumnProps["description"];

  /**
   * Optional call to action for the tab.
   */
  cta?: TextColumnProps["cta"];

  /**
   * The media displayed when the tab is active
   */
  media: ReactNode;

  /**
   * Progress bar duration in seconds.
   *
   * @default 5
   */
  progressDuration?: number;

  /**
   * A function that is called when the progress bar ends.
   */
  onProgressEnd?: () => void;
};

/**
 * ProgressTab is a tab that displays a progress bar when active.
 *
 * On mobile screens, only the progress bar is displayed, and the active tab is visible.
 */
export function ProgressTab({
  isActive,
  titleAs,
  theme = "light",
  progressDuration = 5,
  onProgressEnd,
  ...props
}: ProgressTabProps) {
  return (
    <Tab display="flex" alignItems="start" flex={1} flexDir="column" gap="tiny" textAlign="left" p={0}>
      <ProgressBar
        isActive={isActive}
        theme={theme}
        progressDuration={progressDuration}
        onProgressEnd={onProgressEnd}
      />
      <TextColumn titleAs={titleAs} theme={theme} display={{ base: "none", md: "flex" }} flex={1} {...props} />
    </Tab>
  );
}
